import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Group, Paper, Stack, Text, TextInput, Textarea, Title } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';

import { BasicButton } from '@components/Button';
import {
  CollectionCard,
  ManagementLayout,
  PageTitle,
  PaginatedList,
  SearchInput,
} from '@components/shared';
import entityColor from '@util/entityColor';
import entityIcon from '@util/entityIcon';
import { toErrorMessage } from '@util/toErrorMessage';

function WorkoutCreateForm({ onCreated, onCancel }) {
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  function onSubmit(event) {
    event.preventDefault();
    setSaving(true);
    const body = { name: name.trim() };
    if (notes.trim()) {
      body.notes = notes.trim();
    }
    axios
      .post('/workouts/', body)
      .then(({ data }) => {
        const workout = data.response?.[0];
        toast.success('Workout created');
        onCreated(workout);
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setSaving(false));
  }

  return (
    <Paper p="md" withBorder radius="md" component="form" onSubmit={onSubmit}>
      <Title order={4} mb="sm">
        New workout
      </Title>
      <Stack gap="sm">
        <TextInput
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <Textarea
          label="Notes"
          description="Optional"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          minRows={2}
          autosize
        />
        <Group>
          <BasicButton
            text="Create workout"
            type="submit"
            loading={saving}
            disabled={saving || !name.trim()}
            color="var(--brand-brick)"
            variant="filled"
            style={{
              background: 'var(--brand-brick)',
              borderColor: 'var(--brand-brick)',
            }}
            primary
          />
          <BasicButton
            text="Cancel"
            type="button"
            onClick={onCancel}
            disabled={saving}
            color="gray"
            variant="outline"
          />
        </Group>
      </Stack>
    </Paper>
  );
}

export default function AdminWorkouts() {
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [listKey, setListKey] = useState(0);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  function onCreated(workout) {
    setCreating(false);
    setListKey((key) => key + 1);
    queryClient.invalidateQueries({ queryKey: ['paginated-list'] });
    queryClient.invalidateQueries({ queryKey: ['admin-workouts'] });
    if (workout?.id) {
      navigate(`/workouts/${workout.id}`);
    }
  }

  return (
    <div>
      <PageTitle
        title="Workouts"
        subtitle="Browse the workout library"
        icon={entityIcon.workout()}
        iconColor={entityColor.workout}
        actions={
          !creating ? (
            <BasicButton
              text="Add workout"
              onClick={() => setCreating(true)}
              color={entityColor.workout}
              variant="light"
              leftSection={entityIcon.workout()}
            />
          ) : null
        }
      />
      <ManagementLayout
        createComponent={
          creating ? (
            <WorkoutCreateForm
              onCreated={onCreated}
              onCancel={() => setCreating(false)}
            />
          ) : null
        }
        dataComponent={
          <div>
            <SearchInput
              value={name}
              onChange={setName}
              placeholder="Search by name..."
            />
            <PaginatedList
              key={listKey}
              requestInfo={{
                url: '/retrieve-workouts/',
                data: { name },
              }}
              displayFormatter={(item) => {
                const workout = item.original;
                const setCount = workout.planned_set_count ?? 0;
                const metaText = workout.notes
                  ? workout.notes
                  : `${setCount} set${setCount === 1 ? '' : 's'}`;
                return (
                  <CollectionCard
                    key={workout.id}
                    to={`/workouts/${workout.id}`}
                    title={workout.name}
                    meta={
                      <Text size="sm" c="dimmed" lineClamp={1}>
                        {metaText}
                      </Text>
                    }
                    badge={workout.locked ? 'locked' : workout.status_formatted}
                    badgeColor={workout.locked ? 'gray' : 'cyan'}
                  />
                );
              }}
            />
          </div>
        }
      />
    </div>
  );
}
